import {
  WorkOrderItemSource,
  WorkOrderItemSourceValue,
  WorkOrderItemStatus,
  WorkOrderItemStatusValue,
  WorkOrderItemType,
  WorkOrderItemTypeValue,
} from "features/WorkOrders/constants/work-order-item.constants";
import { QuotationItemType } from "features/Quotations/constants/quotation.constants";
import { billOfQuantitiesDelta } from "features/Quotations/utils/boqFieldArray.utils";
import type {
  WorkOrderItem,
  WorkOrderItemFormRow,
  WorkOrderItemPersistPayload,
} from "features/WorkOrders/types/work-order.types";

/** Planned quantity minus BOQ snapshot; `null` when there is no contract baseline (ADHOC). */
export function workOrderItemQuantityDelta(
  plannedQty: number,
  originalQuantity: number | null | undefined,
): number | null {
  return billOfQuantitiesDelta(plannedQty, originalQuantity);
}

/**
 * Deviation for display: when a contract baseline exists, use current planned
 * qty minus baseline (stays correct after edits). Otherwise prefer API
 * `quantityDeviation` when the server sends it (e.g. enriched GET).
 */
export function workOrderItemDeviationForDisplay(
  line: Pick<WorkOrderItem, "quantityDeviation" | "originalQuantity">,
  plannedQty: number,
): number | null {
  if (
    line.originalQuantity != null &&
    Number.isFinite(Number(line.originalQuantity))
  ) {
    return billOfQuantitiesDelta(plannedQty, line.originalQuantity);
  }
  const qd = line.quantityDeviation;
  if (qd != null && Number.isFinite(Number(qd))) {
    return Number(qd);
  }
  return null;
}

/** Same rules as {@link workOrderItemDeviationForDisplay} for form rows (`qty` = planned). */
export function workOrderItemDeviationForFormRow(
  row: Pick<
    WorkOrderItemFormRow,
    "qty" | "originalQuantity" | "quantityDeviation"
  >,
): number | null {
  return workOrderItemDeviationForDisplay(row, Number(row.qty));
}

const WORK_ORDER_PLANNED_QTY_EPS = 0.01;

/**
 * Minimum planned quantity while the work order is in execution (e.g. IN_PROGRESS).
 * New rows (no persisted id) keep the usual small positive minimum; existing rows cannot plan below executed qty.
 */
export function workOrderPlannedQtyMinForExecutionLine(
  row: Pick<WorkOrderItemFormRow, "id" | "executedQty">,
): number {
  const id = row.id != null ? Number(row.id) : NaN;
  const persisted = Number.isFinite(id) && id > 0;
  if (!persisted) return WORK_ORDER_PLANNED_QTY_EPS;
  const ex = Number(row.executedQty ?? 0);
  const floor = Number.isFinite(ex) && ex >= 0 ? ex : 0;
  return Math.max(WORK_ORDER_PLANNED_QTY_EPS, floor);
}

function isWorkOrderItemSource(v: unknown): v is WorkOrderItemSourceValue {
  return v === WorkOrderItemSource.QUOTATION || v === WorkOrderItemSource.ADHOC;
}

function isWorkOrderItemType(v: unknown): v is WorkOrderItemTypeValue {
  return (
    v === WorkOrderItemType.MATERIAL ||
    v === WorkOrderItemType.LABOR ||
    v === WorkOrderItemType.SERVICE
  );
}

/** Maps API / stored work order line into form row (planned qty lives in `qty`). */
export function mapApiWorkOrderItemToFormRow(
  raw: unknown,
): WorkOrderItemFormRow {
  const item = raw as Record<string, unknown>;
  const planned = Number(item.quantity ?? item.qty ?? 1);
  const safePlanned = Number.isFinite(planned) && planned > 0 ? planned : 1;

  const originalRaw = item.originalQuantity ?? item.original_quantity;
  const originalQuantity =
    originalRaw != null && Number.isFinite(Number(originalRaw))
      ? Number(originalRaw)
      : null;

  const qdRaw = item.quantityDeviation ?? item.quantity_deviation;
  const quantityDeviation =
    qdRaw != null && Number.isFinite(Number(qdRaw)) ? Number(qdRaw) : null;

  const isExtra = Boolean(item.isExtra ?? item.is_extra);
  const isModified = Boolean(item.isModified ?? item.is_modified);

  const sourceType: WorkOrderItemSourceValue = isWorkOrderItemSource(
    item.sourceType,
  )
    ? item.sourceType
    : WorkOrderItemSource.ADHOC;

  const executedRaw = item.executedQty;
  const executedQty =
    executedRaw != null && Number.isFinite(Number(executedRaw))
      ? Number(executedRaw)
      : 0;

  const type: WorkOrderItemTypeValue = isWorkOrderItemType(item.type)
    ? item.type
    : WorkOrderItemType.MATERIAL;

  const status = item.status;
  const statusNorm =
    status === WorkOrderItemStatus.PENDING ||
    status === WorkOrderItemStatus.IN_PROGRESS ||
    status === WorkOrderItemStatus.COMPLETED
      ? status
      : WorkOrderItemStatus.PENDING;

  const rawId = item.id;
  const idNum =
    typeof rawId === "number"
      ? rawId
      : rawId != null && Number.isFinite(Number(rawId))
        ? Number(rawId)
        : undefined;

  const qItemRaw = item.quotationItemId ?? item.quotation_item_id;
  const quotationItemId =
    qItemRaw != null && Number.isFinite(Number(qItemRaw))
      ? Number(qItemRaw)
      : null;

  return {
    id: idNum,
    sourceType,
    quotationItemId,
    productId:
      item.productId != null && item.productId !== ""
        ? Number(item.productId)
        : ("" as const),
    description: typeof item.description === "string" ? item.description : "",
    type,
    originalQuantity,
    quantityDeviation,
    isExtra,
    isModified,
    qty: safePlanned,
    executedQty,
    unit: typeof item.unit === "string" ? item.unit : "",
    rate:
      item.rate != null && Number.isFinite(Number(item.rate))
        ? Number(item.rate)
        : 0,
    amount:
      item.amount != null && Number.isFinite(Number(item.amount))
        ? Number(item.amount)
        : 0,
    status: statusNorm,
    price: 0,
  };
}

/** Build persist payload for API (`quantity` is planned; `originalQuantity` echoed for QUOTATION lines). */
export function formWorkOrderItemRowToPersistPayload(
  row: WorkOrderItemFormRow,
): WorkOrderItemPersistPayload {
  const quantity = Number(row.qty);
  const sourceType = row.sourceType;
  const originalQuantity =
    sourceType === WorkOrderItemSource.QUOTATION ? row.originalQuantity : null;

  const payload: WorkOrderItemPersistPayload = {
    sourceType,
    quotationItemId: row.quotationItemId,
    productId:
      row.productId !== "" && Number.isFinite(Number(row.productId))
        ? Number(row.productId)
        : null,
    description: row.description,
    type: row.type,
    originalQuantity,
    quantity,
    executedQty: row.executedQty,
    unit: row.unit,
    rate: row.rate,
    amount: row.amount,
    status: row.status ?? WorkOrderItemStatus.PENDING,
  };

  if (row.id != null) {
    payload.id = row.id;
  }

  return payload;
}

export function isPersistableWorkOrderItemRow(
  row: WorkOrderItemFormRow,
): boolean {
  const qtyOk = Number.isFinite(Number(row.qty)) && Number(row.qty) > 0;
  if (!qtyOk) return false;

  if (
    row.type === WorkOrderItemType.LABOR ||
    row.type === WorkOrderItemType.SERVICE
  ) {
    return (
      typeof row.description === "string" && row.description.trim().length > 0
    );
  }

  const hasProduct =
    row.productId !== "" &&
    row.productId != null &&
    Number.isFinite(Number(row.productId)) &&
    Number(row.productId) > 0;
  const hasDesc =
    typeof row.description === "string" && row.description.trim().length > 0;
  return hasProduct || hasDesc;
}

/** Shared BOQ row shape (`BoqItemsFieldArray`) + work-order line metadata. */
export type WorkOrderBoqFormRow = {
  type: QuotationItemType;
  description: string;
  productId: number | "" | string;
  quantity: number;
  unit: string;
  rate: number;
  id?: number;
  sourceType: WorkOrderItemSourceValue;
  quotationItemId: number | null;
  originalQuantity: number | null;
  executedQty: number;
  quantityDeviation: number | null;
  isExtra: boolean;
  isModified: boolean;
  price: number;
  status?: WorkOrderItemStatusValue;
};

export function workOrderFormRowToBoqRow(
  row: WorkOrderItemFormRow,
): WorkOrderBoqFormRow {
  return {
    type: row.type as QuotationItemType,
    description: row.description,
    productId:
      row.productId == null || row.productId === "" ? "" : row.productId,
    quantity: Number(row.qty),
    unit: row.unit,
    rate: row.rate,
    id: row.id,
    sourceType: row.sourceType,
    quotationItemId: row.quotationItemId,
    originalQuantity: row.originalQuantity,
    executedQty: row.executedQty,
    quantityDeviation: row.quantityDeviation ?? null,
    isExtra: Boolean(row.isExtra),
    isModified: Boolean(row.isModified),
    price: row.price,
    status: row.status,
  };
}

export function boqRowToWorkOrderFormRow(
  row: WorkOrderBoqFormRow,
): WorkOrderItemFormRow {
  const qty = Number(row.quantity);
  const rate = Number(row.rate) || 0;
  const safeQty = Number.isFinite(qty) && qty > 0 ? qty : 1;
  return {
    id: row.id,
    sourceType: row.sourceType,
    quotationItemId: row.quotationItemId,
    productId:
      row.productId === "" || row.productId == null
        ? ("" as const)
        : Number(row.productId),
    description: String(row.description ?? ""),
    type: (isWorkOrderItemType(row.type)
      ? row.type
      : WorkOrderItemType.MATERIAL) as WorkOrderItemTypeValue,
    originalQuantity: row.originalQuantity,
    quantityDeviation: row.quantityDeviation,
    isExtra: row.isExtra,
    isModified: row.isModified,
    qty: safeQty,
    executedQty: row.executedQty,
    unit: String(row.unit ?? ""),
    rate,
    amount: safeQty * rate,
    status: row.status ?? WorkOrderItemStatus.PENDING,
    price: row.price ?? 0,
  };
}

/** Maps an accepted quotation line into a work-order BOQ row (create flow). */
export function mapQuotationItemToWorkOrderBoqRow(
  item: unknown,
): WorkOrderBoqFormRow {
  const row = item as Record<string, unknown>;
  const quantityRaw = Number(row?.quantity);
  const quantity =
    Number.isFinite(quantityRaw) && quantityRaw > 0 ? quantityRaw : 1;
  const rateRaw = Number(row?.rate);
  const rate = Number.isFinite(rateRaw) ? rateRaw : 0;
  const typeRaw = String(row?.type ?? "");
  const type = (
    isWorkOrderItemType(typeRaw) ? typeRaw : WorkOrderItemType.MATERIAL
  ) as QuotationItemType;
  const productIdRaw = Number(row?.productId);
  const productId =
    Number.isFinite(productIdRaw) && productIdRaw > 0
      ? productIdRaw
      : ("" as const);
  const quotationItemIdRaw = Number(row?.id);
  const quotationItemId =
    Number.isFinite(quotationItemIdRaw) && quotationItemIdRaw > 0
      ? quotationItemIdRaw
      : null;

  return {
    type,
    description: String(row?.description ?? "").trim(),
    productId,
    quantity,
    unit: String(row?.unit ?? "").trim(),
    rate,
    sourceType: WorkOrderItemSource.QUOTATION,
    quotationItemId,
    originalQuantity: quantity,
    executedQty: 0,
    quantityDeviation: 0,
    isExtra: false,
    isModified: false,
    price: 0,
    status: WorkOrderItemStatus.PENDING,
  };
}

export function createEmptyAdhocWorkOrderFormRow(): WorkOrderItemFormRow {
  return {
    sourceType: WorkOrderItemSource.ADHOC,
    quotationItemId: null,
    productId: "",
    description: "",
    type: WorkOrderItemType.MATERIAL,
    originalQuantity: null,
    quantityDeviation: null,
    isExtra: true,
    isModified: false,
    qty: 1,
    executedQty: 0,
    unit: "",
    rate: 0,
    amount: 0,
    status: WorkOrderItemStatus.PENDING,
    price: 0,
  };
}
